import React, { useEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import Button from '@atlaskit/button/new';
import ArrowUpCircleIcon from '@atlaskit/icon/glyph/arrow-up-circle';
import { Box, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

const duration = 700;
const easeOutCurve = 'cubic-bezier(0.2,0,0,1)';

const backToTopStyles = xcss({
	position: 'fixed',
	insetBlockEnd: token('space.200', '16px'),
	insetInlineEnd: token('space.200', '16px'),
	zIndex: 'flag',
	cursor: 'pointer',
	transform: 'translateY(2vh)',
	// Fade in / out related styles
	visibility: 'hidden',
	opacity: 0,
	transition: `opacity ${duration}ms ${easeOutCurve}, transform ${duration}ms ${easeOutCurve}, visibility ${duration}ms ${easeOutCurve}`,
	':hover': {
		opacity: 1,
	},
	// Show only on larger screens
	display: 'none',
	[media.above.sm]: {
		display: 'inline-flex',
	},
});

const visibleStyles = xcss({
	opacity: 1,
	transform: 'none',
	visibility: 'visible',
});

type BackToTopPanelProps = {
	label: string;
};

const BackToTopIcon = () => <ArrowUpCircleIcon size="small" label="" />;

/**
 * __Back to top__
 */
const BackToTop = (props: BackToTopPanelProps) => {
	const viewPortHeight = typeof window !== 'undefined' ? window.innerHeight : 0;

	const [farAwayFromTop, setFarAwayFromTop] = useState(false);

	useEffect(() => {
		const onScroll = debounce(() => {
			const currentPosition = window.pageYOffset;
			setFarAwayFromTop(currentPosition > viewPortHeight * 2);
		}, 100);

		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, [viewPortHeight]);

	const [visible, setVisible] = useState(false);
	const ref = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		const currentRef = ref.current;
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => setVisible(entry.isIntersecting));
		});

		currentRef && observer.observe(currentRef);
		// eslint-disable-next-line react-hooks/exhaustive-deps
		return () => {
			currentRef && observer.unobserve(currentRef);
			observer.disconnect();
		};
	}, [farAwayFromTop]);

	return (
		<Box xcss={[backToTopStyles, visible && farAwayFromTop && visibleStyles]}>
			<Button
				appearance="default"
				ref={ref}
				onClick={() => window.scrollTo(0, 0)}
				iconBefore={BackToTopIcon}
			>
				{props.label}
			</Button>
		</Box>
	);
};

export default BackToTop;
